import type { ColType } from './excelExport';

// Planilha como DADO PURO (cabeçalho, linhas, tipos) — quem gera o arquivo é
// `baixarPlanilhas`. A separação existe para a montagem das abas rodar sob
// `node --test`, sem tocar em DOM nem carregar a biblioteca de Excel.

export interface Planilha {
  nome: string;
  headers: string[];
  rows: (string | number)[][];
  colTypes: ColType[];
  widths: number[];
  totals?: (string | number)[];
}

/** Descritor de coluna: o cabeçalho, o tipo/largura e como tirar o valor do item. */
export interface Coluna<T> {
  header: string;
  tipo: ColType;
  largura: number;
  valor: (item: T, indice: number) => string | number;
}

/** Monta uma Planilha a partir de descritores de coluna e da lista de itens. */
export function montarPlanilha<T>(
  nome: string,
  colunas: Coluna<T>[],
  itens: T[],
  totals?: (string | number)[],
): Planilha {
  return {
    nome,
    headers: colunas.map((c) => c.header),
    rows: itens.map((item, i) => colunas.map((c) => c.valor(item, i))),
    colTypes: colunas.map((c) => c.tipo),
    widths: colunas.map((c) => c.largura),
    totals,
  };
}

/**
 * Linha de totais alinhada às colunas: vazia em tudo, menos no rótulo (1ª
 * coluna) e nas colunas somadas, informadas por cabeçalho.
 */
export function linhaDeTotais<T>(
  colunas: Coluna<T>[],
  rotulo: string,
  somas: Record<string, number>,
): (string | number)[] {
  const linha: (string | number)[] = colunas.map(() => '');
  linha[0] = rotulo;
  Object.entries(somas).forEach(([header, valor]) => {
    const i = colunas.findIndex((c) => c.header === header);
    if (i >= 0) linha[i] = valor;
  });
  return linha;
}

/** Serial de data do Excel (dias desde 30/12/1899). Vazio quando não há data. */
export function dataParaSerialExcel(valor: string | null | undefined): number | '' {
  if (!valor) return '';
  const [ano, mes, dia] = valor.split('T')[0].split('-').map(Number);
  if (!ano || !mes || !dia) return '';
  return (Date.UTC(ano, mes - 1, dia) - Date.UTC(1899, 11, 30)) / 86400000;
}

/**
 * Gera e baixa o .xlsx. A biblioteca de Excel (xlsx-js-style) entra por import
 * dinâmico: é pesada e só faz sentido carregar quando alguém clica em exportar.
 * `nomeBase` recebe o carimbo de data/hora no fim.
 */
export async function baixarPlanilhas(planilhas: Planilha[], nomeBase: string): Promise<void> {
  const { buildSheet, downloadWorkbook, fileStamp } = await import('./excelExport');
  const sheets = planilhas.map((p) => ({
    name: p.nome,
    ws: buildSheet(p.headers, p.rows, p.colTypes, { widths: p.widths, totals: p.totals, autofilter: p.rows.length > 0 }),
  }));
  downloadWorkbook(sheets, `${nomeBase}_${fileStamp()}.xlsx`);
}
